AFRAME.registerComponent('timeline-control', {
    schema: {
        timestamps: { type: 'array', default: [] }, // Format: ["YYYY-MM-DD HH:MM:SS[.000 TZ]" or "YYYY-MM-DD"]
        width: { type: 'number', default: 1 },
        height: { type: 'number', default: 0.05 },
        fontSize: { type: 'number', default: 0.1 },
        interval: { type: 'string', default: '1d' },
        previousCloseDate: { type: 'string', default: '' },
        dataSource: { type: 'string', default: '' }
    },

    init: function () {
        if (__LOG_INFO__) AppLog.info('timeline-control init')
        this.timestamps = [];
        this.currentIndex = 0;
        this.isDragging = false;
        this.controller = null;
        this.isAnimating = false;
        this.currentAnimationSession = 0;
        this.startDate = null;
        this.endDate = null;
        this.cameraEl = this.el.sceneEl.cameraEl;

        if (__LOG_INFO__) AppLog.info('Initializing timeline-control component');

        this.isMarketMapOverlayActive = () => document?.body?.classList?.contains('mmap-graph');

        this.isETTimeZone = true;
        try {
            const rawSettings = localStorage.getItem('mainSettings');
            if (rawSettings) {
                const parsed = JSON.parse(rawSettings);
                if (parsed && parsed.value && parsed.value.isETTimeZone === false) {
                    this.isETTimeZone = false;
                }
            }
        } catch (e) {
            if (__LOG_WARN__) AppLog.warn('Failed to read timezone from localStorage', e);
        }

        document.addEventListener('timezone-changed', (e) => {
            if (e.detail && typeof e.detail.isETTimeZone === 'boolean') {
                if (__LOG_INFO__) AppLog.info(`[Timeline] Timezone changed to: ${e.detail.isETTimeZone ? 'ET' : 'Local'}`);
                this.isETTimeZone = e.detail.isETTimeZone;
                if (this.timestamps.length > 0) {
                    this.updateTimeIndicators();
                    this.updateTimeline(this.timestamps[this.currentIndex], this.currentIndex, this.timestamps.length);
                }
            }
        });

        this.isAnimationOn = true;
        try {
            const configureData = localStorage.getItem('configureControlSettings');
            if (configureData) {
                const parsed = JSON.parse(configureData);
                if (parsed && parsed.value && typeof parsed.value.isAnimationOn === 'boolean') {
                    this.isAnimationOn = parsed.value.isAnimationOn;
                }
            }
        } catch (e) {
            if (__LOG_WARN__) AppLog.warn('Failed to read isAnimationOn from localStorage', e);
        }

        setTimeout(() => {
            this.updatePlayButtonIcon(this.isAnimationOn);
        }, 100);

        document.addEventListener('configure-changed', (e) => {
            if (e.detail && e.detail.settings && typeof e.detail.settings.isAnimationOn === 'boolean') {
                const newState = e.detail.settings.isAnimationOn;
                if (__LOG_INFO__) AppLog.info(`[Timeline] Animation setting changed to: ${newState}`);
                this.isAnimationOn = newState;
                this.updatePlayButtonIcon(newState);
                if (!newState) {
                    this.stopAnimation();
                    if (this.timestamps.length > 0) {
                        this.currentIndex = this.timestamps.length - 1;
                        this.updateTimeline(this.timestamps[this.currentIndex], this.currentIndex, this.timestamps.length);
                    }
                } else {
                    this.stopAnimation();
                    this.goToBeginning();
                }
            }
        });

        this.createTimelineUI();

        this.slider.addEventListener('mousedown', (event) => {
            if (this.isMarketMapOverlayActive()) {
                return;
            }
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();
            this.isDragging = true;
            this.disableCameraControls();
            if (__LOG_INFO__) AppLog.info('Mouse down on slider, dragging started');
            this.updateSliderFromMouse(event);
        });

        document.addEventListener('mousemove', (event) => {
            if (this.isDragging && !this.isMarketMapOverlayActive()) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                this.updateSliderFromMouse(event);
            }
        }, { capture: true });

        document.addEventListener('mouseup', () => {
            if (this.isDragging) {
                this.isDragging = false;
                this.enableCameraControls();
                if (__LOG_INFO__) AppLog.info('Mouse up, dragging ended');
                this.emitGotoTime();
            }
        });

        this.slider.addEventListener('triggerdown', (event) => {
            event.stopPropagation();
            this.isDragging = true;
            this.controller = event.target;
            if (__LOG_INFO__) AppLog.info('VR trigger down on slider');
        });

        this.el.sceneEl.addEventListener('triggerup', () => {
            if (this.isDragging) {
                this.isDragging = false;
                this.controller = null;
                if (__LOG_INFO__) AppLog.info('VR trigger up');
                this.emitGotoTime();
            }
        });

        this.slider.addEventListener('click', (event) => {
            if (this.isMarketMapOverlayActive()) {
                return;
            }
            event.stopPropagation();
            if (!this.isDragging && event.detail.intersection) {
                this.updateSliderFromRaycast(event.detail.intersection.point);
                this.emitGotoTime();
            }
        });

        this.el.addEventListener('timeline-update', (event) => {
            const { timestamp, index, total } = event.detail;
            this.updateTimeline(timestamp, index, total);
        });

        this.tick = AFRAME.utils.throttle(this.tick, 16, this);

        this.el.sceneEl.addEventListener('loaded', () => {
            if (__LOG_INFO__) AppLog.info('Scene loaded, forcing render');
            this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, this.el.sceneEl.camera);
        });
    },

    createTimelineUI: function () {
        const container = this.el;

        if (__LOG_INFO__) AppLog.info('Creating timeline UI with a-troika-text labels');

        this.timelineBar = document.createElement('a-box');
        this.timelineBar.setAttribute('width', this.data.width);
        this.timelineBar.setAttribute('height', this.data.height);
        this.timelineBar.setAttribute('depth', 0.1);
        this.timelineBar.setAttribute('color', '#333');
        this.timelineBar.setAttribute('position', '0 0 0');
        this.timelineBar.setAttribute('data-raycastable', '');
        this.timelineBar.setAttribute('visible', true);
        container.appendChild(this.timelineBar);
        if (__LOG_INFO__) AppLog.info('Timeline bar created: width=', this.data.width, 'height=', this.data.height);

        this.slider = document.createElement('a-box');
        this.slider.setAttribute('width', 0.05);
        this.slider.setAttribute('height', this.data.height + 0.05);
        this.slider.setAttribute('depth', 0.11);
        this.slider.setAttribute('color', '#00FF00');
        this.slider.setAttribute('position', '0 0 0.01');
        this.slider.setAttribute('data-raycastable', '');
        container.appendChild(this.slider);
        if (__LOG_INFO__) AppLog.info('Slider created: position=0 0 0.01');

        this.timestampText = document.createElement('a-troika-text');
        this.timestampText.setAttribute('value', '');
        this.timestampText.setAttribute('font-size', this.data.fontSize);
        this.timestampText.setAttribute('align', 'center');
        this.timestampText.setAttribute('color', 'white');
        this.timestampText.setAttribute('position', `0 ${this.data.height + 0.1} 0.01`);
        this.timestampText.setAttribute('visible', false);
        container.appendChild(this.timestampText);

        this.dateRangeText = document.createElement('a-troika-text');
        this.dateRangeText.setAttribute('value', '');
        this.dateRangeText.setAttribute('font-size', this.data.fontSize);
        this.dateRangeText.setAttribute('align', 'center');
        this.dateRangeText.setAttribute('color', 'white');
        this.dateRangeText.setAttribute('position', `0 ${this.data.height + 0.1} 0.01`);
        this.dateRangeText.setAttribute('visible', false);
        container.appendChild(this.dateRangeText);

        this.timeIndicators = [];
        const positions = [-this.data.width / 2, -this.data.width / 4, 0, this.data.width / 4, this.data.width / 2];
        positions.forEach((x, i) => {
            const indicator = document.createElement('a-troika-text');
            indicator.setAttribute('value', '');
            indicator.setAttribute('font-size', this.data.fontSize * 0.8);
            indicator.setAttribute('align', 'center');
            indicator.setAttribute('color', '#AAA');
            indicator.setAttribute('position', `${x} ${-this.data.height - 0.05} 0.01`);
            indicator.setAttribute('visible', true);
            container.appendChild(indicator);
            this.timeIndicators.push(indicator);
            if (__LOG_INFO__) AppLog.info(`timeIndicator[${i}] created: position=${x} ${-this.data.height - 0.05} 0.01, font-size=`, this.data.fontSize * 0.8, 'visible=', true);
        });

        this.toBeginningButton = document.createElement('a-plane');
        this.toBeginningButton.setAttribute('position', `-0.34 ${-this.data.height - 0.18} 0.01`);
        this.toBeginningButton.setAttribute('width', '0.14');
        this.toBeginningButton.setAttribute('height', '0.1');
        this.toBeginningButton.setAttribute('material', 'color: white; opacity: 0; transparent: true; shader: standard');

        const beginningBars = document.createElement('a-entity');
        beginningBars.setAttribute('position', '-0.05 0 0');
        const bar1 = document.createElement('a-box');
        bar1.setAttribute('width', '0.015');
        bar1.setAttribute('height', '0.1');
        bar1.setAttribute('depth', '0.01');
        bar1.setAttribute('color', 'white');
        bar1.setAttribute('position', '-0.015 0 0');
        const bar2 = document.createElement('a-box');
        bar2.setAttribute('width', '0.015');
        bar2.setAttribute('height', '0.1');
        bar2.setAttribute('depth', '0.01');
        bar2.setAttribute('color', 'white');
        bar2.setAttribute('position', '0.015 0 0');
        beginningBars.appendChild(bar1);
        beginningBars.appendChild(bar2);
        this.toBeginningButton.appendChild(beginningBars);

        const beginningTriangle = document.createElement('a-triangle');
        beginningTriangle.setAttribute('vertex-a', '0 0.1 0');
        beginningTriangle.setAttribute('vertex-b', '-0.05 -0.05 0');
        beginningTriangle.setAttribute('vertex-c', '0.05 -0.05 0');
        beginningTriangle.setAttribute('rotation', '0 0 90');
        beginningTriangle.setAttribute('scale', '.6 .5 .6');
        beginningTriangle.setAttribute('position', '0.02 0 0');
        beginningTriangle.setAttribute('material', 'color: white; shader: standard');
        this.toBeginningButton.appendChild(beginningTriangle);

        this.toBeginningButton.setAttribute('data-raycastable', '');
        this.toBeginningButton.addEventListener('click', () => {
            this.goToBeginning();
            beginningTriangle.setAttribute('material', 'color', 'grey');
            bar1.setAttribute('color', 'grey');
            bar2.setAttribute('color', 'grey');
            setTimeout(() => {
                beginningTriangle.setAttribute('material', 'color', 'white');
                bar1.setAttribute('color', 'white');
                bar2.setAttribute('color', 'white');
            }, 200);
        });
        this.toBeginningButton.addEventListener('mouseenter', () => {
            beginningTriangle.setAttribute('material', 'color', 'cyan');
            bar1.setAttribute('color', 'cyan');
            bar2.setAttribute('color', 'cyan');
        });
        this.toBeginningButton.addEventListener('mouseleave', () => {
            beginningTriangle.setAttribute('material', 'color', 'white');
            bar1.setAttribute('color', 'white');
            bar2.setAttribute('color', 'white');
        });
        container.appendChild(this.toBeginningButton);
        if (__LOG_INFO__) AppLog.info('toBeginningButton created');

        this.backButton = document.createElement('a-triangle');
        this.backButton.setAttribute('vertex-a', '0 0.1 0');
        this.backButton.setAttribute('vertex-b', '-0.05 -0.05 0');
        this.backButton.setAttribute('vertex-c', '0.05 -0.05 0');
        this.backButton.setAttribute('position', `-0.17 ${-this.data.height - 0.18} 0.01`);
        this.backButton.setAttribute('rotation', '0 0 90');
        this.backButton.setAttribute('scale', '.6 .6 .6');
        this.backButton.setAttribute('material', 'color: white; shader: standard');
        this.backButton.setAttribute('data-raycastable', '');
        this.backButton.addEventListener('click', () => {
            this.stepBackward();
            this.backButton.setAttribute('material', 'color', 'grey');
            setTimeout(() => this.backButton.setAttribute('material', 'color', 'white'), 200);
        });
        this.backButton.addEventListener('mouseenter', () => this.backButton.setAttribute('material', 'color', 'cyan'));
        this.backButton.addEventListener('mouseleave', () => this.backButton.setAttribute('material', 'color', 'white'));
        container.appendChild(this.backButton);
        if (__LOG_INFO__) AppLog.info('backButton created');

        this.playButton = document.createElement('a-triangle');
        this.playButton.setAttribute('vertex-a', '0 0.1 0');
        this.playButton.setAttribute('vertex-b', '-0.05 -0.05 0');
        this.playButton.setAttribute('vertex-c', '0.05 -0.05 0');
        this.playButton.setAttribute('position', `0 ${-this.data.height - 0.18} 0.01`);
        this.playButton.setAttribute('rotation', '0 0 -90');
        this.playButton.setAttribute('scale', '1 .7 1');
        this.playButton.setAttribute('material', 'color: white; shader: standard');
        this.playButton.setAttribute('data-raycastable', '');
        this.playButton.addEventListener('click', () => {
            this.runFromCurrent();
            this.playButton.setAttribute('material', 'color', 'grey');
            setTimeout(() => this.playButton.setAttribute('material', 'color', 'white'), 200);
        });
        this.playButton.addEventListener('mouseenter', () => this.playButton.setAttribute('material', 'color', 'cyan'));
        this.playButton.addEventListener('mouseleave', () => this.playButton.setAttribute('material', 'color', 'white'));
        container.appendChild(this.playButton);
        if (__LOG_INFO__) AppLog.info('playButton created');

        this.forwardButton = document.createElement('a-triangle');
        this.forwardButton.setAttribute('vertex-a', '0 0.1 0');
        this.forwardButton.setAttribute('vertex-b', '-0.05 -0.05 0');
        this.forwardButton.setAttribute('vertex-c', '0.05 -0.05 0');
        this.forwardButton.setAttribute('position', `0.17 ${-this.data.height - 0.18} 0.01`);
        this.forwardButton.setAttribute('rotation', '0 0 -90');
        this.forwardButton.setAttribute('scale', '.6 .6 .6');
        this.forwardButton.setAttribute('material', 'color: white; shader: standard');
        this.forwardButton.setAttribute('data-raycastable', '');
        this.forwardButton.addEventListener('click', () => {
            this.stepForward();
            this.forwardButton.setAttribute('material', 'color', 'grey');
            setTimeout(() => this.forwardButton.setAttribute('material', 'color', 'white'), 200);
        });
        this.forwardButton.addEventListener('mouseenter', () => this.forwardButton.setAttribute('material', 'color', 'cyan'));
        this.forwardButton.addEventListener('mouseleave', () => this.forwardButton.setAttribute('material', 'color', 'white'));
        container.appendChild(this.forwardButton);
        if (__LOG_INFO__) AppLog.info('forwardButton created');

        this.toEndButton = document.createElement('a-plane');
        this.toEndButton.setAttribute('position', `0.34 ${-this.data.height - 0.18} 0.01`);
        this.toEndButton.setAttribute('width', '0.14');
        this.toEndButton.setAttribute('height', '0.1');
        this.toEndButton.setAttribute('material', 'color: white; opacity: 0; transparent: true; shader: standard');

        const endTriangle = document.createElement('a-triangle');
        endTriangle.setAttribute('vertex-a', '0 0.1 0');
        endTriangle.setAttribute('vertex-b', '-0.05 -0.05 0');
        endTriangle.setAttribute('vertex-c', '0.05 -0.05 0');
        endTriangle.setAttribute('rotation', '0 0 -90');
        endTriangle.setAttribute('scale', '.6 .5 .6');
        endTriangle.setAttribute('position', '-0.02 0 0');
        endTriangle.setAttribute('material', 'color: white; shader: standard');
        this.toEndButton.appendChild(endTriangle);

        const endBars = document.createElement('a-entity');
        endBars.setAttribute('position', '0.05 0 0');
        const endBar1 = document.createElement('a-box');
        endBar1.setAttribute('width', '0.015');
        endBar1.setAttribute('height', '0.1');
        endBar1.setAttribute('depth', '0.01');
        endBar1.setAttribute('color', 'white');
        endBar1.setAttribute('position', '-0.015 0 0');
        const endBar2 = document.createElement('a-box');
        endBar2.setAttribute('width', '0.015');
        endBar2.setAttribute('height', '0.1');
        endBar2.setAttribute('depth', '0.01');
        endBar2.setAttribute('color', 'white');
        endBar2.setAttribute('position', '0.015 0 0');
        endBars.appendChild(endBar1);
        endBars.appendChild(endBar2);
        this.toEndButton.appendChild(endBars);

        this.toEndButton.setAttribute('data-raycastable', '');
        this.toEndButton.addEventListener('click', () => {
            this.goToEnd();
            endTriangle.setAttribute('material', 'color', 'grey');
            endBar1.setAttribute('color', 'grey');
            endBar2.setAttribute('color', 'grey');
            setTimeout(() => {
                endTriangle.setAttribute('material', 'color', 'white');
                endBar1.setAttribute('color', 'white');
                endBar2.setAttribute('color', 'white');
            }, 200);
        });
        this.toEndButton.addEventListener('mouseenter', () => {
            endTriangle.setAttribute('material', 'color', 'cyan');
            endBar1.setAttribute('color', 'cyan');
            endBar2.setAttribute('color', 'cyan');
        });
        this.toEndButton.addEventListener('mouseleave', () => {
            endTriangle.setAttribute('material', 'color', 'white');
            endBar1.setAttribute('color', 'white');
            endBar2.setAttribute('color', 'white');
        });
        container.appendChild(this.toEndButton);
        if (__LOG_INFO__) AppLog.info('toEndButton created');

        this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, this.el.sceneEl.camera);
        if (__LOG_INFO__) AppLog.info('Initial UI render forced');

        const toBeginButtonHTML = document.querySelector('.player-container .to-begin');
        const backButtonHTML = document.querySelector('.player-container .back');
        const playButtonHTML = document.querySelector('.player-container .play');
        const forwardButtonHTML = document.querySelector('.player-container .forward');
        const toEndButtonHTML = document.querySelector('.player-container .to-end');
        if (__LOG_INFO__) AppLog.info('HTML buttons selected', toBeginButtonHTML, backButtonHTML, playButtonHTML, forwardButtonHTML, toEndButtonHTML)

        const createWrappedHandler = (originalHandler) => {
            const self = this;
            return function (...args) {
                if (self.isMarketMapOverlayActive()) {
                    return;
                }
                return originalHandler.apply(self, args);
            };
        };

        if (toBeginButtonHTML) {
            toBeginButtonHTML.addEventListener('click', createWrappedHandler(this.goToBeginning.bind(this)));
        }
        if (backButtonHTML) {
            backButtonHTML.addEventListener('click', createWrappedHandler(this.stepBackward.bind(this)));
        }
        if (playButtonHTML) {
            playButtonHTML.addEventListener('click', createWrappedHandler(this.runFromCurrent.bind(this)));
        }
        if (forwardButtonHTML) {
            forwardButtonHTML.addEventListener('click', createWrappedHandler(this.stepForward.bind(this)));
        }
        if (toEndButtonHTML) {
            toEndButtonHTML.addEventListener('click', createWrappedHandler(this.goToEnd.bind(this)));
        }
    },

    update: function (oldData) {
        if (this.data.dataSource !== oldData.dataSource) {
            this.dataSource = this.data.dataSource;
        }
        
        if (this.data.interval !== oldData.interval) {
            this.interval = this.data.interval;

            this.timestamps = [];
            this.currentIndex = 0;
            this.startDate = this.endDate = null;
            this.isAnimating = false;
            this.currentAnimationSession = (this.currentAnimationSession || 0) + 9999;
            this.updatePlayButtonIcon && this.updatePlayButtonIcon(false);

            const mainChartTime = document.querySelector("#main-container .title-and-date p");
            if (mainChartTime) mainChartTime.textContent = '';
        }
        
        if (this.data.previousCloseDate !== oldData.previousCloseDate) {
            this.previousCloseDate = this.data.previousCloseDate;
            if (this.timestamps.length > 0) {
                this.updateTimeline(this.timestamps[this.currentIndex], this.currentIndex, this.timestamps.length);
            }
        }
        
        if (this.data.timestamps !== oldData.timestamps && this.data.timestamps.length > 0) {
            if (__LOG_INFO__) AppLog.info('Processing timestamps:', this.data.timestamps);

            this.isAnimating = false;
            this.currentAnimationSession = (this.currentAnimationSession || 0) + 9999;
            this.updatePlayButtonIcon && this.updatePlayButtonIcon(false);

            this.timestamps = this.data.timestamps.map(ts => {
                if (typeof ts === 'string') {
                    if (ts.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        return `${ts} 16:00:00`;
                    }
                    return ts.replace(/\.\d{3}\s*(EST|EDT|CST|CDT|PST|PDT)?$/, '');
                }
                return ts;
            });

            if (__LOG_INFO__) AppLog.info('Processed timestamps:', this.timestamps);

            if (this.timestamps.length > 0) {
                const firstTs = this.timestamps[0].split(' ')[0];
                const lastTs = this.timestamps[this.timestamps.length - 1].split(' ')[0];
                this.startDate = new Date(firstTs + 'T00:00:00Z');
                this.endDate = new Date(lastTs + 'T00:00:00Z');
            } else {
                this.startDate = this.endDate = null;
            }

            this.updateTimeIndicators();
            const initialIdx = this.isAnimationOn ? 0 : this.timestamps.length - 1;
            this.currentIndex = initialIdx;
            this.updateTimeline(this.timestamps[initialIdx], initialIdx, this.timestamps.length);
            if (__LOG_INFO__) AppLog.info('Forcing render after update');
            this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, this.el.sceneEl.camera);
        }
    },

    updateTimeline: function (timestamp, index, total) {
        if (!this.timestamps.length && total > 0) {
            if (__LOG_WARN__) AppLog.warn('No timestamps, initializing placeholders:', total);
            this.timestamps = Array(total).fill().map((_, i) => `Timestamp ${i}`);
            this.startDate = this.endDate = null;
            this.updateTimeIndicators();
        }

        const mainChartTime = document.querySelector("#main-container .title-and-date p")
        const currentTimestamp = timestamp || this.timestamps[index];
        
        const isPercentChangeChart = !['dividend', 'dividend-sec', 'sip', 'sip-sec'].includes(this.data.dataSource);
        
        let dataAndTimeToDisplay;
        if (isPercentChangeChart && this.data.previousCloseDate) {
            dataAndTimeToDisplay = this.formatDateRange(currentTimestamp, this.data.previousCloseDate, this.data.interval);
        } else {
            const dateAndTime = this.formatDateAndTime(currentTimestamp);
            dataAndTimeToDisplay = `${dateAndTime.date} ${dateAndTime.time}`;
        }
        
        mainChartTime.innerText = dataAndTimeToDisplay;

        let localIdx = this.timestamps.indexOf(currentTimestamp);
        if (localIdx === -1) {
            const datePart = (typeof currentTimestamp === 'string') ? currentTimestamp.split(' ')[0] : '';
            if (datePart) {
                localIdx = this.timestamps.findIndex(ts => ts.split(' ')[0] === datePart);
            }
        }
        if (localIdx !== -1) {
            this.currentIndex = localIdx;
        } else if (index >= 0 && index < this.timestamps.length) {
            this.currentIndex = index;
        }
        const sliderX = total > 1 ? (index / (total - 1)) * this.data.width - (this.data.width / 2) : (total === 1 ? this.data.width / 2 : 0);
        this.slider.object3D.position.set(sliderX, 0, 0.01);
        this.slider.object3D.updateMatrix();

        const htmlProgressContainer = document.querySelector('.player-container .progress-container');
        const htmlMarker = document.querySelector('.player-container .progress-container .marker');
        if (htmlMarker && htmlProgressContainer) {
            const percent = total > 1 ? index / (total - 1) : (total === 1 ? 1 : 0);
            htmlMarker.style.left = `${percent * 100}%`;
        }

        const isSameDay = this.startDate && this.endDate &&
            this.startDate.getUTCFullYear() === this.endDate.getUTCFullYear() &&
            this.startDate.getUTCMonth() === this.endDate.getUTCMonth() &&
            this.startDate.getUTCDate() === this.endDate.getUTCDate();

        if (isSameDay) {
            const parts = timestamp ? timestamp.split(' ') : [''];
            const timeStr = this.getDisplayTime(timestamp);
            this.timestampText.setAttribute('value', timeStr);
            this.timestampText.setAttribute('visible', true);
            this.dateRangeText.setAttribute('visible', false);
        } else {
            if (timestamp) {
                const datePart = timestamp.split(' ')[0];
                const currentDate = new Date(datePart + 'T00:00:00Z');

                if (!isNaN(currentDate.getTime())) {
                    const month = (currentDate.getUTCMonth() + 1).toString();
                    const day = currentDate.getUTCDate().toString();
                    const year = currentDate.getUTCFullYear();
                    const currentDateStr = `${month}/${day}/${year.toString().slice(-2)}`;

                    this.dateRangeText.setAttribute('value', currentDateStr);
                    this.dateRangeText.setAttribute('visible', true);
                    this.timestampText.setAttribute('visible', false);
                } else {
                    this.dateRangeText.setAttribute('value', 'N/A');
                    this.dateRangeText.setAttribute('visible', true);
                    this.timestampText.setAttribute('visible', false);
                }
            } else {
                this.dateRangeText.setAttribute('value', 'N/A');
                this.dateRangeText.setAttribute('visible', true);
                this.timestampText.setAttribute('visible', false);
            }
        }

    },

    showDateRange: function () {
        if (this.startDate && this.endDate) {
            const startYear = this.startDate.getUTCFullYear();
            const endYear = this.endDate.getUTCFullYear();
            const startMonth = (this.startDate.getUTCMonth() + 1).toString().padStart(2, '0');
            const endMonth = (this.endDate.getUTCMonth() + 1).toString().padStart(2, '0');
            const startDay = this.startDate.getUTCDate().toString().padStart(2, '0');
            const endDay = this.endDate.getUTCDate().toString().padStart(2, '0');
            const rangeStr = startYear !== endYear
                ? `${startYear}-${startMonth}-${startDay} - ${endYear}-${endMonth}-${endDay}`
                : `${startMonth}/${startDay} - ${endMonth}/${endDay}`;
            this.dateRangeText.setAttribute('value', rangeStr);
            this.dateRangeText.setAttribute('visible', true);
            this.timestampText.setAttribute('visible', false);
            this.dateRangeText.object3D.updateMatrix();

        } else {
            this.dateRangeText.setAttribute('value', 'No valid dates');
            this.dateRangeText.setAttribute('visible', true);
            this.timestampText.setAttribute('visible', false);
            this.dateRangeText.object3D.updateMatrix();
        }
    },

    updateTimeIndicators: function () {
        if (__LOG_INFO__) AppLog.info('Updating timeIndicators');
        if (__LOG_INFO__) AppLog.info('Timestamps to update indicators:', this.timestamps);

        if (this.timestamps.length < 1) {
            if (__LOG_WARN__) AppLog.warn('Not enough timestamps to update indicators:', this.timestamps.length);
            this.timeIndicators.forEach((indicator, i) => {
                indicator.setAttribute('value', '');
                try {
                    if (indicator && indicator.object3D) {
                        indicator.object3D.visible = false;
                        indicator.object3D.updateMatrix();
                    } else if (indicator) {
                        indicator.setAttribute('visible', false);
                        indicator.addEventListener('loaded', () => {
                            try { if (indicator.object3D) { indicator.object3D.visible = false; indicator.object3D.updateMatrix(); } } catch(e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Deferred hide failed for timeIndicator['+i+']', e); } catch(_) {} }
                        }, { once: true });
                    }
                } catch (e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Hide guard failed for timeIndicator['+i+']', e); } catch(_) {} }
            });
            return;
        }

        const isSameDay = this.startDate && this.endDate &&
            this.startDate.getFullYear() === this.endDate.getFullYear() &&
            this.startDate.getMonth() === this.endDate.getMonth() &&
            this.startDate.getDate() === this.endDate.getDate();

        const htmlRange = document.querySelector('.player-container .legend');
        if (htmlRange) {
            htmlRange.innerHTML = '';
        }

        const n = this.timestamps.length;
        let indices;
        if (n === 1) {
            indices = [null, null, null, null, 0];
        } else if (n === 2) {
            indices = [0, null, null, null, 1];
        } else if (n === 3) {
            indices = [0, null, 1, null, 2];
        } else if (n === 4) {
            indices = [0, 1, null, 2, 3];
        } else {
            const step = Math.floor((n - 1) / 4);
            indices = [0, step, step * 2, step * 3, n - 1];
        }

        if (isSameDay) {
            this.timeIndicators.forEach((indicator, i) => {
                const idx = indices[i];
                if (idx === null || idx === undefined) {
                    indicator.setAttribute('value', '');
                    if (indicator.object3D) {
                        indicator.object3D.visible = false;
                    } else {
                        indicator.setAttribute('visible', false);
                    }
                    return;
                }
                const timeStr = this.getDisplayTime(this.timestamps[idx]);
                indicator.setAttribute('value', timeStr);
                try {
                    if (indicator && indicator.object3D) {
                        indicator.object3D.visible = true;
                        indicator.object3D.updateMatrix();
                    } else if (indicator) {
                        indicator.setAttribute('visible', true);
                        indicator.addEventListener('loaded', () => {
                            try { if (indicator.object3D) { indicator.object3D.visible = true; indicator.object3D.updateMatrix(); } } catch(e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Deferred show failed for timeIndicator['+i+']', e); } catch(_) {} }
                        }, { once: true });
                    }
                } catch (e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Show guard failed for timeIndicator['+i+']', e); } catch(_) {} }
                if (htmlRange) {
                    htmlRange.innerHTML += `<div class="item">${timeStr}</div>`;
                }
            });
        } else {
            if (__LOG_INFO__) AppLog.info('Multi-day dataset, showing dates');
            const selectedTimestamps = indices.map(idx => idx !== null ? this.timestamps[idx] : null);

            const indicatorDates = [];
            const sameYear = this.startDate && this.endDate && this.startDate.getFullYear() === this.endDate.getFullYear();

            selectedTimestamps.forEach((timestamp, i) => {
                if (!timestamp) {
                    indicatorDates.push('');
                    return;
                }

                const datePart = (typeof timestamp === 'string') ? timestamp.split(' ')[0] : '';
                if (!datePart) {
                    indicatorDates.push('');
                    return;
                }
                const date = new Date(datePart + 'T00:00:00Z');
                if (isNaN(date.getTime())) {
                    indicatorDates.push('');
                    return;
                }
                const month = (date.getUTCMonth() + 1).toString();
                const day = date.getUTCDate().toString();
                const year = date.getUTCFullYear();
                const dateStr = sameYear ? `${month}/${day}` : `${year}-${month}-${day}`;
                indicatorDates.push(dateStr);
            });

            this.timeIndicators.forEach((indicator, i) => {
                const val = indicatorDates[i];
                if (!val) {
                    indicator.setAttribute('value', '');
                    if (indicator.object3D) {
                        indicator.object3D.visible = false;
                    } else {
                        indicator.setAttribute('visible', false);
                    }
                    return;
                }
                indicator.setAttribute('value', val);
                try {
                    if (indicator && indicator.object3D) {
                        indicator.object3D.visible = true;
                        indicator.object3D.updateMatrix();
                    } else if (indicator) {
                        indicator.setAttribute('visible', true);
                        indicator.addEventListener('loaded', () => {
                            try { if (indicator.object3D) { indicator.object3D.visible = true; indicator.object3D.updateMatrix(); } } catch(e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Deferred show failed for timeIndicator['+i+']', e); } catch(_) {} }
                        }, { once: true });
                    }
                } catch (e) { try { if (__LOG_INFO__) AppLog.info('[timeline-control] Show guard failed for timeIndicator['+i+']', e); } catch(_) {} }

                if (htmlRange) {
                    htmlRange.innerHTML += `<div class="item">${val}</div>`;
                }
            });
            if (__LOG_INFO__) AppLog.info('Multi-day timeIndicators set to:', indicatorDates);
        }

    },

    stopAnimation: function () {
        if (this.isAnimating) {
            this.isAnimating = false;
            if (this.currentAnimationSession !== undefined) {
                this.currentAnimationSession++;
            }
            this.updatePlayButtonIcon(false);
            if (__LOG_INFO__) AppLog.info('Animation stopped');
        }
    },

    goToBeginning: function () {
        if (this.timestamps.length === 0) return;
        this.stopAnimation();
        this.currentIndex = 0;
        const timestamp = this.timestamps[this.currentIndex];
        if (__LOG_INFO__) AppLog.info(`Going to beginning: index=${this.currentIndex}, timestamp=${timestamp}`);
        this.updateTimeline(timestamp, this.currentIndex, this.timestamps.length);
        this.emitGotoTime();

        if (window.gtag) gtag('event', `Desktop_CenterPanel_AnimationControl`);
    },

    goToEnd: function () {
        if (this.timestamps.length === 0) return;
        this.stopAnimation();
        this.currentIndex = this.timestamps.length - 1;
        const timestamp = this.timestamps[this.currentIndex];
        if (__LOG_INFO__) AppLog.info(`Going to end: index=${this.currentIndex}, timestamp=${timestamp}`);
        this.updateTimeline(timestamp, this.currentIndex, this.timestamps.length);
        this.emitGotoTime();

        if (window.gtag) gtag('event', `Desktop_CenterPanel_AnimationControl`);
    },

    stepForward: function () {
        if (this.timestamps.length === 0) return;
        this.stopAnimation();
        if (this.currentIndex < this.timestamps.length - 1) {
            this.currentIndex++;
            const timestamp = this.timestamps[this.currentIndex];
            if (__LOG_INFO__) AppLog.info(`Stepping forward: index=${this.currentIndex}, timestamp=${timestamp}`);
            this.updateTimeline(timestamp, this.currentIndex, this.timestamps.length);
            this.emitGotoTime();

            if (window.gtag) gtag('event', `Desktop_CenterPanel_AnimationControl`);
        }
    },

    stepBackward: function () {
        if (__LOG_INFO__) AppLog.info(`Stepping backward: this.currentIndex=${this.currentIndex}`);
        if (this.timestamps.length === 0) return;
        this.stopAnimation();
        if (this.currentIndex > 0) {
            this.currentIndex--;
            const timestamp = this.timestamps[this.currentIndex];
            if (__LOG_INFO__) AppLog.info(`Stepping backward: index=${this.currentIndex}, timestamp=${timestamp}`);
            this.updateTimeline(timestamp, this.currentIndex, this.timestamps.length);
            this.emitGotoTime();

            if (window.gtag) gtag('event', `Desktop_CenterPanel_AnimationControl`);
        }
    },

    runFromCurrent: async function () {
        if (this.timestamps.length === 0) return;

        if (this.isAnimating) {
            this.stopAnimation();
            return;
        }

        this.isAnimating = true;
        this.currentAnimationSession = (this.currentAnimationSession || 0) + 1;
        const session = this.currentAnimationSession;
        this.updatePlayButtonIcon(true);
        if (__LOG_INFO__) AppLog.info(`Running from index=${this.currentIndex}`);

        for (let i = this.currentIndex; i < this.timestamps.length; i++) {
            if (!this.isAnimating || this.currentAnimationSession !== session) {
                return;
            }
            const timestamp = this.timestamps[i];
            if (__LOG_INFO__) AppLog.info(`Animating to index=${i}, timestamp=${timestamp}`);
            this.updateTimeline(timestamp, i, this.timestamps.length);
            this.el.emit('goto-time', { timestamp, index: i }, true);
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        if (this.currentAnimationSession === session) {
            this.isAnimating = false;
            this.updatePlayButtonIcon(false);
        }

        if (window.gtag) gtag('event', `Desktop_CenterPanel_AnimationControl`);
    },

    updatePlayButtonIcon: function (isPlaying) {
        const playButtonHTML = document.querySelector('.player-container .play');
        const playImg = playButtonHTML ? playButtonHTML.querySelector('img') : null;
        if (playButtonHTML && playImg) {
            if (isPlaying) {
                playButtonHTML.classList.add('playing');
                playButtonHTML.setAttribute('title', 'Pause');
                playImg.src = './desktop_assets/main-block/player/pause.svg';
            } else {
                playButtonHTML.classList.remove('playing');
                playButtonHTML.setAttribute('title', 'Play');
                playImg.src = './desktop_assets/main-block/player/play.svg';
            }
        }
    },

    updateSliderFromMouse: function (event) {
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        const camera = this.el.sceneEl.camera;
        raycaster.setFromCamera(mouse, camera);

        const intersects = raycaster.intersectObject(this.timelineBar.object3D, true);
        if (intersects.length > 0) {
            const point = intersects[0].point;
            const localPoint = this.timelineBar.object3D.worldToLocal(point.clone());
            const clampedX = Math.max(-this.data.width / 2, Math.min(this.data.width / 2, localPoint.x));

            this.slider.object3D.position.set(clampedX, 0, 0.01);
            this.slider.object3D.updateMatrix();

            if (this.timestamps.length > 0) {
                const fraction = (clampedX + this.data.width / 2) / this.data.width;
                this.currentIndex = Math.round(fraction * (this.timestamps.length - 1));
                if (__LOG_INFO__) AppLog.info(`Slider moved: index=${this.currentIndex}, timestamp=${this.timestamps[this.currentIndex]}`);
                this.updateTimeline(this.timestamps[this.currentIndex], this.currentIndex, this.timestamps.length);
            }
        }
    },

    updateSliderFromRaycast: function (point) {
        const localPoint = this.timelineBar.object3D.worldToLocal(point.clone());
        const clampedX = Math.max(-this.data.width / 2, Math.min(this.data.width / 2, localPoint.x));
        this.slider.object3D.position.set(clampedX, 0, 0.01);
        this.slider.object3D.updateMatrix();

        if (this.timestamps.length > 0) {
            const fraction = (clampedX + this.data.width / 2) / this.data.width;
            this.currentIndex = Math.round(fraction * (this.timestamps.length - 1));
            this.updateTimeline(this.timestamps[this.currentIndex], this.currentIndex, this.timestamps.length);
        }
    },

    disableCameraControls: function () {
        if (this.cameraEl) {
            const controls = this.cameraEl.components['look-controls'];
            if (controls) {
                controls.data.enabled = false;
                if (__LOG_INFO__) AppLog.info('Camera look-controls disabled');
            }
        }
    },

    enableCameraControls: function () {
        if (this.cameraEl) {
            const controls = this.cameraEl.components['look-controls'];
            if (controls) {
                controls.data.enabled = true;
                if (__LOG_INFO__) AppLog.info('Camera look-controls enabled');
            }
        }
    },

    getDisplayTime: function (etTimestamp) {
        if (!etTimestamp) return '';

        const to12Hour = (h, m) => {
            const isPM = h >= 12;
            const hour12 = h % 12 || 12;
            return `${hour12}:${m.toString().padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
        };

        if (this.isETTimeZone) {
            const parts = etTimestamp.split(' ');
            if (!parts[1]) return '';

            const [h, m] = parts[1].substring(0, 5).split(':').map(Number);
            return to12Hour(h, m);
        } else {
            try {
                const [datePart, timePart] = etTimestamp.split(' ');
                if (!datePart || !timePart) return '';

                const d = new Date(`${datePart}T${timePart}Z`);

                const isMatch = (dateObj, targetHour, targetMinute) => {
                    const parts = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/New_York',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: false
                    }).formatToParts(dateObj);

                    const h = parseInt(parts.find(p => p.type === 'hour').value, 10) % 24;
                    const m = parseInt(parts.find(p => p.type === 'minute').value, 10);
                    return h === targetHour && m === targetMinute;
                };

                const [hStr, mStr] = timePart.split(':');
                const targetH = parseInt(hStr, 10);
                const targetM = parseInt(mStr, 10);

                let validUTC = new Date(d.getTime() + 5 * 60 * 60 * 1000);

                if (!isMatch(validUTC, targetH, targetM)) {
                    validUTC = new Date(d.getTime() + 4 * 60 * 60 * 1000);
                }

                const localH = validUTC.getHours();
                const localM = validUTC.getMinutes();

                return to12Hour(localH, localM);

            } catch (e) {
                if (__LOG_WARN__) AppLog.warn('Error converting ET to Local:', e);
                const parts = etTimestamp.split(' ');
                if (!parts[1]) return '';

                const [h, m] = parts[1].substring(0, 5).split(':').map(Number);
                return to12Hour(h, m);
            }
        }
    },
    getDisplayDate: function (etTimestamp) {
        if (!etTimestamp) return '';
        const [year, month, day] = etTimestamp.split('-').map(Number);
        if (!year || !month || !day) return '';
        return `${month}/${day}/${year.toString().slice(-2)}`;
    },

    formatDateRange: function(currentTimestamp, previousCloseDate, interval) {
        if (!currentTimestamp || !previousCloseDate) {
            if (__LOG_WARN__) AppLog.warn('formatDateRange: missing timestamp or previousCloseDate', { currentTimestamp, previousCloseDate });
            return (typeof currentTimestamp === 'string') ? currentTimestamp.split(' ')[0] : '';
        }
        try {
            const currentDatePart = currentTimestamp.split(' ')[0];
            const currentTimePart = currentTimestamp.split(' ')[1] || '16:00:00';

            const currentDate = new Date(currentDatePart + 'T00:00:00Z');
            const previousDate = new Date(previousCloseDate + 'T00:00:00Z');

            if (isNaN(currentDate.getTime()) || isNaN(previousDate.getTime())) {
                if (__LOG_WARN__) AppLog.warn('Invalid dates for range formatting', { currentDatePart, previousCloseDate });
                return currentDatePart;
            }

            const formatDateShort = (date) => {
                const month = date.getUTCMonth() + 1;
                const day = date.getUTCDate();
                return `${month}/${day}`;
            };

            const currentDateFormatted = formatDateShort(currentDate);
            const previousDateFormatted = formatDateShort(previousDate);

            const timeFormatter = new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });

            if (interval && interval.toLowerCase() === '1d') {
                const currentDisplayTime = this.formatDateAndTime(currentTimestamp).time;

                return `${currentDateFormatted} ${currentDisplayTime} vs. ${previousDateFormatted} EOD`;
            } else {
                return `${currentDateFormatted} EOD vs. ${previousDateFormatted} EOD`;
            }
        } catch (error) {
            if (__LOG_ERROR__) AppLog.error('Error formatting date range:', error);
            return (typeof currentTimestamp === 'string') ? currentTimestamp.split(' ')[0] : '';
        }
    },

    formatDateAndTime: function(sourceDateAndTime) {
        if (!sourceDateAndTime) return { time: '', date: '' };
        
        const cleanIso = sourceDateAndTime.trim().replace(/\s[A-Z]{3,4}$/, '').replace(' ', 'T');

        let dateObj;

        if (this.isETTimeZone) {
            dateObj = new Date(cleanIso);
        } else {
            const tempDate = new Date(cleanIso);
            const nyString = tempDate.toLocaleString('en-US', { timeZone: 'America/New_York' });
            const nyDate = new Date(nyString);
            const offsetDiff = tempDate - nyDate;

            dateObj = new Date(tempDate.getTime() + offsetDiff);
        }
        
        const timeFormatter = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            ...(!this.isETTimeZone && { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
        });

        const dateFormatter = new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
            ...(!this.isETTimeZone && { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
        });

        return {
            time: timeFormatter.format(dateObj),
            date: dateFormatter.format(dateObj)
        };
    },

    tick: function () {
        if (this.isDragging && this.controller) {
            const raycaster = this.controller.components.raycaster;
            if (raycaster && raycaster.intersections.length > 0) {
                const intersection = raycaster.intersections.find(i => i.object.el === this.timelineBar);
                if (intersection) {
                    this.updateSliderFromRaycast(intersection.point);
                }
            }
        }
    },

    emitGotoTime: function () {
        const timestamp = this.timestamps[this.currentIndex];
        if (__LOG_INFO__) AppLog.info(`Emitting goto-time: timestamp=${timestamp}, index=${this.currentIndex}`);
        this.el.emit('goto-time', { timestamp, index: this.currentIndex }, true);
    }
});
